import { useEffect, useState } from 'react';
import { getAddedValue } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';

const CAPACITY_CARDS = [
  { key: 'non_executive',             label: 'Non-executive' },
  { key: 'executive_twin',            label: 'Executive twin' },
  { key: 'board_advisor',             label: 'Board advisor' },
  { key: 'visiting_professor',        label: 'Visiting professor' },
  { key: 'absolute_service_provider', label: 'Absolute service provider' },
];

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)',
  letterSpacing: '0.1em', textTransform: 'uppercase',
};
const INPUT = {
  fontFamily: 'inherit', fontSize: 12, background: 'var(--paper-2)',
  border: '1px solid var(--hairline)', padding: '5px 8px',
  color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
};
const BACK_BTN = {
  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--ink-3)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
};
const SAVE_BTN = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
  background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)',
  padding: '7px 16px', cursor: 'pointer',
};
const CANCEL_BTN = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
  background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--hairline)',
  padding: '7px 16px', cursor: 'pointer',
};

function statusChip(text) {
  if (text?.trim()) {
    return <span className="chip" style={{ background: 'var(--pos-soft)', borderColor: 'var(--pos)', color: 'var(--accent-ink)' }}>DRAFTED</span>;
  }
  return <span className="chip muted">OPEN</span>;
}

function CapacityEditor({ label, text, onSave, onBack }) {
  const [draft, setDraft] = useState(text ?? '');

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label={label} meta="ADDED VALUE" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        <div className="bj-field">
          <div style={MONO_LABEL}>How you deliver in this capacity</div>
          <textarea
            style={{ ...INPUT, minHeight: 160, resize: 'vertical', lineHeight: 1.6 }}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Describe how you operate in this capacity, the value you bring, and typical engagements…"
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave(draft)}>Save</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function AddedDetail({ onClose }) {
  const [capacities, setCapacities] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getAddedValue()
      .then((d) => { if (!cancelled) setCapacities(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (editingKey) {
    const card = CAPACITY_CARDS.find(c => c.key === editingKey);
    return (
      <DetailShell eyebrow="08 · Delivery Dashboard" title="Added Value" onClose={onClose}>
        <CapacityEditor
          label={card.label}
          text={capacities?.[editingKey] ?? ''}
          onSave={(text) => {
            setCapacities(prev => ({ ...prev, [editingKey]: text }));
            setEditingKey(null);
          }}
          onBack={() => setEditingKey(null)}
        />
      </DetailShell>
    );
  }

  return (
    <DetailShell eyebrow="08 · Delivery Dashboard" title="Added Value" onClose={onClose}>
      <div className="card">
        <SectionTitle label="Capacity definition" meta={`${CAPACITY_CARDS.length} MODES`} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16 }}>
          {CAPACITY_CARDS.map((card) => {
            const text = capacities?.[card.key] ?? '';
            const status = text.trim() ? 'drafted' : 'todo';

            return (
              <div
                className={'bj-card' + (status === 'todo' ? ' is-empty' : '')}
                key={card.key}
              >
                <div className="bj-card-label">{card.label}</div>
                <div className="bj-card-body">
                  {text.trim()
                    ? <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.55 }}>
                        {text.length > 120 ? text.slice(0, 120) + '…' : text}
                      </div>
                    : <div className="bj-empty-mark">— awaiting capture —</div>
                  }
                </div>
                <div className="bj-card-foot">
                  {statusChip(text)}
                  <button className="bj-edit" onClick={() => setEditingKey(card.key)}>
                    {status === 'todo' ? 'Start' : 'Edit'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="tiny" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
          <span className="mono">ADDED VALUE · {CAPACITY_CARDS.filter(c => (capacities?.[c.key] ?? '').trim()).length} OF {CAPACITY_CARDS.length} DEFINED</span>
        </div>
      </div>
    </DetailShell>
  );
}

export default AddedDetail;
